export type MysteryRoomCharacterDirection = 'down' | 'up' | 'left' | 'right' | 'downLeft' | 'downRight';

export type MysteryRoomCharacterFrame = {
  key: string;
  url: string;
};

export type MysteryRoomCharacterSkin = {
  id: string;
  name: string;
  menuUrl: string;
  worldDisplay: {
    width: number;
    height: number;
    offsetY: number;
  };
  frames: Record<MysteryRoomCharacterDirection, MysteryRoomCharacterFrame[]>;
  idle: MysteryRoomCharacterFrame;
};

const ANNA_BASE_URL = '/games/mystery-room/characters/anna';

function annaFrame(name: string): MysteryRoomCharacterFrame {
  return {
    key: `mr_character_anna_${name.replace(/[^a-z0-9]+/gi, '_')}`,
    url: `${ANNA_BASE_URL}/${name}.png`,
  };
}

export const MYSTERY_ROOM_ANNA_SKIN: MysteryRoomCharacterSkin = {
  id: 'anna',
  name: 'Anna',
  menuUrl: `${ANNA_BASE_URL}/hero_anna_idle.png`,
  worldDisplay: {
    width: 65,
    height: 101,
    offsetY: -18,
  },
  idle: annaFrame('hero_anna_idle'),
  frames: {
    down: [
      annaFrame('hero_anna_walk_down1'),
      annaFrame('hero_anna_walk_down2'),
      annaFrame('hero_anna_walk_down3'),
      annaFrame('hero_anna_walk_down4'),
    ],
    up: [
      annaFrame('hero_anna_walk_top1'),
      annaFrame('hero_anna_walk_top2'),
      annaFrame('hero_anna_walk_top3'),
      annaFrame('hero_anna_walk_top4'),
    ],
    left: [
      annaFrame('hero_anna_walk_left1'),
      annaFrame('hero_anna_walk_left2'),
      annaFrame('hero_anna_walk_left3'),
      annaFrame('hero_anna_walk_left4'),
    ],
    right: [
      annaFrame('hero_anna_walk_right1'),
      annaFrame('hero_anna_walk_right2'),
      annaFrame('hero_anna_walk_right3'),
      annaFrame('hero_anna_walk_right4'),
    ],
    downLeft: [
      annaFrame('hero_anna_walk_down_left1'),
      annaFrame('hero_anna_walk_down_left2'),
      annaFrame('hero_anna_walk_down_left3'),
      annaFrame('hero_anna_walk_down_left4'),
    ],
    downRight: [
      annaFrame('hero_anna_walk_down_right'),
      annaFrame('hero_anna_walk_down_right2'),
      annaFrame('hero_anna_walk_down_right3'),
      annaFrame('hero_anna_walk_down_right4'),
    ],
  },
};

export const MYSTERY_ROOM_DEFAULT_CHARACTER_ID = MYSTERY_ROOM_ANNA_SKIN.id;
export const MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN = MYSTERY_ROOM_ANNA_SKIN;

export function resolveMysteryRoomCharacterFrame(
  skin: MysteryRoomCharacterSkin,
  facing: 'up' | 'down' | 'left' | 'right',
  animationState: 'idle' | 'walk',
  frameTimeMs: number,
  movement?: { x: number; y: number } | null,
): MysteryRoomCharacterFrame {
  if (animationState === 'idle') {
    return skin.idle;
  }

  const direction = resolveCharacterDirection(facing, movement);
  const frames = skin.frames[direction] ?? skin.frames.down;
  return frames[Math.floor(frameTimeMs / 120) % frames.length] ?? skin.idle;
}

function resolveCharacterDirection(
  facing: 'up' | 'down' | 'left' | 'right',
  movement?: { x: number; y: number } | null,
): MysteryRoomCharacterDirection {
  const x = movement?.x ?? 0;
  const y = movement?.y ?? 0;
  const absX = Math.abs(x);
  const absY = Math.abs(y);

  if (absX > 0.05 && absY > 0.05 && y > 0) {
    return x < 0 ? 'downLeft' : 'downRight';
  }

  if (absX > absY && absX > 0.05) {
    return x < 0 ? 'left' : 'right';
  }

  if (absY > 0.05) {
    return y < 0 ? 'up' : 'down';
  }

  return facing === 'up' ? 'up' : facing;
}
